import React, { useEffect, useState } from "react";
import { Box, Grid, Slider, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import useFetchGenericRQScoringConfigById from "../../hooks/useFetchGenericRQScoringConfigById";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAuth } from "../../contexts/AuthContext";
import { getGenericResearchQueryDetail } from "../../services/ResearchQueriesService";
import { SLIDER_ACCENT } from "../../components/Scoring/scoreTheme";
import { IconBtnEditBase } from "../../common/utils/icons";

const SLIDER_SX = {
  color: SLIDER_ACCENT,
  "& .MuiSlider-thumb": { color: SLIDER_ACCENT },
  "& .MuiSlider-track": { color: SLIDER_ACCENT, border: "none" },
  "& .MuiSlider-rail": { color: SLIDER_ACCENT, opacity: 0.3 },
  "& .MuiSlider-valueLabel": { backgroundColor: SLIDER_ACCENT },
  "& .MuiSlider-markActive": { backgroundColor: SLIDER_ACCENT },
};

const SLIDER_MARKS = [
  { value: 0, label: "0" },
  { value: 0.5, label: "0.5" },
  { value: 1, label: "1" },
];

const ViewGenericRQScoring: React.FC = () => {
  const { id: configId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(
    "generic_research_query_scoring_config",
    "edit",
  );
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const [isLoading, setIsLoading] = useState(false);
  const [queryName, setQueryName] = useState<string | null>(null);

  const [configData] = useFetchGenericRQScoringConfigById({
    setIsLoading,
    configId: configId ? parseInt(configId) : undefined,
  });

  useEffect(() => {
    const embedded = configData?.generic_research_query?.name;
    if (embedded) {
      setQueryName(embedded);
      return;
    }
    if (configData?.generic_research_query_id) {
      getGenericResearchQueryDetail(configData.generic_research_query_id)
        .then((rq: any) => {
          if (rq?.name) setQueryName(rq.name);
        })
        .catch(() => {});
    }
  }, [
    configData?.generic_research_query?.name,
    configData?.generic_research_query_id,
  ]);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: `Config #${configId}`,
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  const field = (label: string, value: any, md: number = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <label className="label-text">{label}</label>
      <div className="form-control">{value ?? "—"}</div>
    </Grid>
  );

  const sliderField = (
    label: string,
    value: number | undefined,
    md: number = 6,
  ) => (
    <Grid item xs={12} md={md}>
      <label className="label-text">
        {label}: {(value ?? 0).toFixed(2)}
      </label>
      <Slider
        value={value ?? 0}
        min={0}
        max={1}
        step={0.05}
        marks={SLIDER_MARKS}
        valueLabelDisplay="auto"
        disabled
        sx={SLIDER_SX}
      />
    </Grid>
  );

  const factorField = (label: string, value: number | undefined) => (
    <Grid item xs={12} md={4}>
      <label className="label-text">{label}</label>
      <TextField
        type="number"
        value={value ?? ""}
        disabled
        InputProps={{ readOnly: true }}
        className="form-control"
      />
    </Grid>
  );

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <h3>View Generic RQ Scoring Config</h3>
        </Box>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        >
          {field(
            "Research Query",
            queryName
              ? `${queryName} (${configData?.generic_research_query_id})`
              : configData?.generic_research_query_id,
          )}
          {canEdit && (
            <Grid
              item
              xs={12}
              sm={6}
              md
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() => navigate(`/scoring/generic-rq/edit/${configId}`)}
              >
                <Box className="d-block">
                  <IconBtnEditBase ariaLabel="Edit Generic RQ Scoring Config" />
                </Box>
              </button>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          sx={{ mt: "20px" }}
        >
          {sliderField(
            "Impact of Record Level",
            configData?.impact_of_record_level,
          )}
          {sliderField(
            "Impact of Issue Level",
            configData?.impact_of_issue_level,
          )}
        </Grid>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          sx={{ mt: "20px" }}
        >
          {factorField("Factor (Low)", configData?.factor_low)}
          {factorField("Factor (Medium)", configData?.factor_medium)}
          {factorField("Factor (High)", configData?.factor_high)}
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewGenericRQScoring;
